import { useDarkMode } from "../../../context/ThemeContext";
import { getThemeClasses } from "../../../utils/ThemeClasses";
import { useForm, Controller } from "react-hook-form";
import { useReportsStore } from "../../../store/reports/reportsStore";
import { useEffect } from "react";
import { useAlert } from "../../../context/AlertContext";
import InfiniteDeviceSelect, { useReportDeviceIds } from "../../../components/InfiniteDeviceSelect";
import { useReportCompanyScope } from "../hooks/useReportCompanyScope";
import ReportActionButton from "../components/ReportActionButton";
import ReportDateRangePicker from "../components/ReportDateRangePicker";
import ReportFormatToggle from "../components/ReportFormatToggle";
import { toLocalInputValue } from "../utils/reportDateFormat";

interface TrailerFormData {
  devices: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
}

export default function TrailerSection() {

  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const generateHistoryReport = useReportsStore((state) => state.generateHistoryReport);
  const loading = useReportsStore((state) => state.loading);
  const { loadedDeviceIds, loadedCount, totalDevices } = useReportDeviceIds();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TrailerFormData>({
    mode: "onBlur",
    defaultValues: {
      devices: [],
      startDate: "",
      endDate: "",
      typeReport: "trailers",
      typeDocument: "pdf",
    },
  });

  const { showAlert } = useAlert();
  const { disableWithoutCompany } = useReportCompanyScope(setValue);

  const selectedDevices = watch("devices");

  const handleSelectAll = () => {
    if (selectedDevices?.length === loadedDeviceIds.length) {
      setValue("devices", []);
    } else {
      setValue("devices", loadedDeviceIds);
    }
  };

  const onSubmit = async (formData: TrailerFormData) => {
    const result = await generateHistoryReport({
      deviceIds: formData.devices,
      startDate: formData.startDate,
      endDate: formData.endDate,
      typeReport: formData.typeReport,
      typeDocument: formData.typeDocument,
    }, showAlert, () => { });

    if (result.success && result.data) {
      const extension = formData.typeDocument === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([result.data], {
        type: formData.typeDocument === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.fileName || `reporte.${extension}`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    setValue("startDate", toLocalInputValue(new Date()));
    setValue("endDate", toLocalInputValue(new Date(new Date().setDate(new Date().getDate() + 15))));
  }, []);

  return (
    <div className="h-full w-full overflow-y-auto p-2 sm:p-4">
      <form onSubmit={handleSubmit(onSubmit)} className={`flex flex-col lg:flex-row ${themeClasses.cardBorder} rounded-lg ${themeClasses.cardBg} p-3 sm:p-3.5 gap-3 justify-between lg:items-end`}>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
          <div className="flex flex-col gap-1 min-w-52 w-full lg:w-auto">
            <div className="flex justify-between items-center">
              <label htmlFor="devices" className={`${themeClasses.text} text-xs`}>
                Equipos {selectedDevices?.length > 0 && `(${selectedDevices.length}/${totalDevices})`}
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={disableWithoutCompany}
                className={`text-xs px-2 py-1 rounded ${themeClasses.buttonBg} text-white hover:opacity-80 disabled:opacity-50`}
              >
                {selectedDevices?.length === loadedDeviceIds.length && loadedCount > 0 ? "Deseleccionar todos" : `Seleccionar todos (${loadedCount})`}
              </button>
            </div>
            <div>
              <Controller
                name="devices"
                control={control}
                rules={{ required: "Selecciona al menos un equipo" }}
                render={({ field }) => (
                  <InfiniteDeviceSelect
                    value={field.value}
                    onChange={field.onChange}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                  />
                )}
              />
              {errors.devices && <span className="text-red-500 text-sm">{errors.devices.message}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Rango de fechas</label>
            <input type="hidden" {...register("startDate", { required: "Fecha inicio requerida" })} />
            <input type="hidden" {...register("endDate", { required: "Fecha fin requerida" })} />
            <ReportDateRangePicker
              startValue={watch("startDate")}
              endValue={watch("endDate")}
              onChangeStart={(v) => setValue("startDate", v, { shouldValidate: true, shouldDirty: true })}
              onChangeEnd={(v) => setValue("endDate", v, { shouldValidate: true, shouldDirty: true })}
              startError={errors.startDate?.message}
              endError={errors.endDate?.message}
            />
          </div>
          <div className="flex flex-col gap-1 w-full lg:w-auto">
            <label className={`${themeClasses.text} text-xs`}>Formato</label>
            <Controller
              name="typeDocument"
              control={control}
              render={({ field }) => <ReportFormatToggle value={field.value} onChange={field.onChange} />}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 w-full lg:w-auto">
        <ReportActionButton
          type="submit"
          disabled={loading || disableWithoutCompany}
        >
          {loading ? "Generando..." : "Generar Reporte"}
        </ReportActionButton>
        </div>
      </form>
    </div>
  );
}
